import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel as ChakraFormLabel,
  FormErrorMessage,
  Heading,
  Input,
  Spinner,
  Text,
  Flex,
  SimpleGrid,
  Icon,
} from "@chakra-ui/react";
import { FiDollarSign, FiEdit2, FiPlusCircle, FiSave } from "react-icons/fi";
import styled from "@emotion/styled";
import { useSetRecoilState } from "recoil";
import { toastState } from "atoms/toastState";
import useAxios from "axios-hooks";
import logger from "infra/logger";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import {
  PAYOUT_CONFIG_FIELDS,
  PAYOUT_CONFIG_DEFAULTS,
  payoutConfigValidationSchema,
  payoutConfigFormValues,
  buildPayoutConfigPayload,
  formatPayoutConfigInputValue,
} from "../variables/payoutConfigFields";

const Configurations = (props) => {
  const { station } = props;

  // State for payout configuration
  const [payoutConfig, setPayoutConfig] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // React Hook Form
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(payoutConfigValidationSchema),
    mode: "onChange",
    defaultValues: { ...PAYOUT_CONFIG_DEFAULTS },
  });

  // Recoil state for toast notifications
  const setToast = useSetRecoilState(toastState);

  // API calls for fetching, creating, and updating payout config
  const [{ loading: configLoading }, fetchConfig] = useAxios(
    { method: "GET", url: `/api/stations/payoutConfig/${station.Id}` },
    { manual: true },
  );

  const [{ loading: createLoading }, createConfig] = useAxios(
    { method: "POST", url: `/api/stations/payoutConfig/${station.Id}` },
    { manual: true },
  );

  const [{ loading: updateLoading }, updateConfig] = useAxios(
    {
      method: "PATCH",
      url: `/api/stations/payoutConfig/${payoutConfig?.Id}`,
    },
    { manual: true },
  );

  // Fetch payout configuration when component mounts
  const getPayoutConfig = useCallback(async () => {
    try {
      const response = await fetchConfig();
      if (response.data && response.data.data) {
        setPayoutConfig(response.data.data);
        reset(payoutConfigFormValues(response.data.data));
      }
    } catch (error) {
      if (error.code === "ERR_CANCELED") {
        return;
      }
      logger.log(error);
      setToast({
        isOpen: true,
        title: "Error fetching payout configuration",
        status: "error",
        duration: 5000,
      });
    }
  }, [fetchConfig, reset, setToast]);

  // Create new payout configuration
  const handleCreateConfig = async (data) => {
    try {
      const payload = buildPayoutConfigPayload(data, station.StationId);

      const response = await createConfig({ data: payload });
      if (response.data && response.data.data) {
        setPayoutConfig(response.data.data);
        setIsEditing(false);
        setToast({
          isOpen: true,
          title: "Payout configuration created successfully",
          status: "success",
          duration: 5000,
        });
      }
    } catch (error) {
      logger.error("Error creating payout config:", error);
      setToast({
        isOpen: true,
        title: "Error creating payout configuration",
        status: "error",
        duration: 5000,
      });
    }
  };

  // Update existing payout configuration
  const handleUpdateConfig = async (data) => {
    try {
      // Create a payload with only modified fields
      const payload = { StationId: station.StationId };
      let hasChanges = false;

      PAYOUT_CONFIG_FIELDS.forEach((field) => {
        const newVal = parseFloat(data[field.name]);
        if (payoutConfig[field.name] !== newVal) {
          payload[field.name] = newVal;
          hasChanges = true;
        }
      });

      // If no changes, show toast and return
      if (!hasChanges) {
        setIsEditing(false);
        setToast({
          isOpen: true,
          title: "Nothing to update",
          status: "warning",
          duration: 5000,
        });
        return;
      }

      // Send update request with only changed fields
      const response = await updateConfig({ data: payload });
      if (response.data && response.data.data) {
        setPayoutConfig(response.data.data);
        setIsEditing(false);
        setToast({
          isOpen: true,
          title: "Payout configuration updated successfully",
          status: "success",
          duration: 5000,
        });
      }
    } catch (error) {
      logger.error("Error updating payout config:", error);
      setToast({
        isOpen: true,
        title: "Error updating payout configuration",
        status: "error",
        duration: 5000,
      });
    }
  };

  // Handle form submission
  const onSubmit = (data) => {
    if (payoutConfig) {
      handleUpdateConfig(data);
    } else {
      handleCreateConfig(data);
    }
  };

  useEffect(() => {
    if (station && station.Id) {
      getPayoutConfig();
    }
  }, [station, getPayoutConfig]);

  // Populate form with existing values when editing mode is enabled
  useEffect(() => {
    if (isEditing && payoutConfig) {
      reset(payoutConfigFormValues(payoutConfig));
    }
  }, [isEditing, payoutConfig, reset]);

  // Loading state
  const isLoading = configLoading || createLoading || updateLoading;

  // Styled components for glass morphism effect
  const GlassCard = styled(Box)`
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(245, 125, 54, 0.15);
    border-radius: 20px;
    padding: 24px;
    transition: all 0.3s ease;
    box-shadow: 0 8px 32px rgba(31, 38, 135, 0.1);

    &:hover {
      box-shadow: 0 8px 32px rgba(31, 38, 135, 0.15);
    }
  `;

  const StyledButton = styled(Button)`
    background: ${(props) =>
      props.variant === "outline" ? "transparent" : "#f57d36"};
    color: ${(props) => (props.variant === "outline" ? "#f57d36" : "white")};
    border: ${(props) =>
      props.variant === "outline" ? "1px solid #f57d36" : "none"};
    transition: all 0.3s ease;

    &:hover {
      background: ${(props) =>
        props.variant === "outline" ? "rgba(245, 125, 54, 0.1)" : "#e06826"};
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0);
    }
  `;

  const FormLabel = styled(ChakraFormLabel)`
    color: #4a5568;
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 6px;
    position: relative;
    display: inline-block;

    &:after {
      content: "";
      position: absolute;
      bottom: -3px;
      left: 0;
      width: 16px;
      height: 2px;
      background: rgba(245, 125, 54, 0.5);
      border-radius: 1px;
    }
  `;

  const InputGroup = styled(Box)`
    margin-bottom: 18px;
    position: relative;
  `;

  const StyledInput = styled(Input)`
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(245, 125, 54, 0.2);
    border-radius: 8px;
    height: 40px;
    max-width: 200px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.8);
      border-color: rgba(245, 125, 54, 0.3);
    }

    &:focus {
      background: rgba(255, 255, 255, 0.9);
      border-color: #f57d36;
      box-shadow: 0 0 0 1px rgba(245, 125, 54, 0.3);
    }

    &::placeholder {
      color: rgba(160, 174, 192, 0.7);
    }
  `;

  const SectionTitle = styled(Heading)`
    color: #2d3748;
    position: relative;
    display: inline-block;
    margin-bottom: 24px;

    &:after {
      content: "";
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 40px;
      height: 4px;
      background: #f57d36;
      border-radius: 2px;
    }
  `;

  // Render empty state when no config exists
  if (!isLoading && !payoutConfig && !isEditing) {
    return (
      <GlassCard textAlign="center" py={10}>
        <Box mb={6}>
          <Icon as={FiDollarSign} boxSize={12} color="gray.400" />
        </Box>
        <Heading size="md" mb={4} color="gray.700">
          No Payout Configuration
        </Heading>
        <Text mb={6} color="gray.500">
          This charging station doesn't have a payout configuration yet.
        </Text>
        <StyledButton
          leftIcon={<FiPlusCircle />}
          onClick={() => setIsEditing(true)}
          isDisabled={isLoading}
        >
          Create Configuration
        </StyledButton>
      </GlassCard>
    );
  }

  // Render loading state
  if (isLoading && !payoutConfig && !isEditing) {
    return (
      <GlassCard
        display="flex"
        justifyContent="center"
        alignItems="center"
        py={10}
      >
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="#f57d36"
          size="xl"
        />
      </GlassCard>
    );
  }

  // Render form for creating/editing
  if (isEditing || (!payoutConfig && !isLoading)) {
    return (
      <GlassCard>
        <SectionTitle size="md">
          {payoutConfig
            ? "Edit Payout Configuration"
            : "Create Payout Configuration"}
        </SectionTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <SimpleGrid
            columns={{ base: 1, sm: 2, lg: 3, xl: 4 }}
            spacing={6}
            maxW="100%"
          >
            {PAYOUT_CONFIG_FIELDS.map((field) => (
              <InputGroup key={field.name}>
                <Flex direction="column">
                  <FormLabel htmlFor={field.name}>{field.label}</FormLabel>
                  <Controller
                    name={field.name}
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <FormControl isInvalid={errors[field.name]}>
                        <StyledInput
                          id={field.name}
                          type={field.type}
                          step={field.step}
                          placeholder={field.placeholder}
                          onChange={(e) => {
                            const val = e.target.value;
                            onChange(val === "" ? "" : parseFloat(val));
                          }}
                          onBlur={onBlur}
                          value={formatPayoutConfigInputValue(value)}
                          ref={ref}
                        />
                        {errors[field.name] && (
                          <FormErrorMessage>
                            {errors[field.name].message}
                          </FormErrorMessage>
                        )}
                      </FormControl>
                    )}
                  />
                </Flex>
              </InputGroup>
            ))}
          </SimpleGrid>

          <Flex justifyContent="flex-end" mt={4} gap={3}>
            <StyledButton
              variant="outline"
              onClick={() => {
                setIsEditing(false);
                if (!payoutConfig) {
                  reset({ ...PAYOUT_CONFIG_DEFAULTS });
                } else {
                  reset(payoutConfigFormValues(payoutConfig));
                }
              }}
            >
              Cancel
            </StyledButton>
            <StyledButton
              type="submit"
              isLoading={isLoading}
              leftIcon={<FiSave />}
            >
              {payoutConfig ? "Update" : "Create"}
            </StyledButton>
          </Flex>
        </form>
      </GlassCard>
    );
  }

  // Render view mode (when config exists and not editing)
  return (
    <GlassCard>
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <SectionTitle size="md">Payout Configuration</SectionTitle>
        <StyledButton
          leftIcon={<FiEdit2 />}
          variant="outline"
          onClick={() => setIsEditing(true)}
          size="sm"
        >
          Edit
        </StyledButton>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        {PAYOUT_CONFIG_FIELDS.map((field) => (
          <Box key={field.name}>
            <Text fontWeight="semibold" color="gray.500" fontSize="sm">
              {field.shortLabel}
            </Text>
            <Text fontWeight="bold" fontSize="lg">
              {field.unit === "₹" ? "₹" : ""}
              {payoutConfig[field.name]?.toFixed(2)}
              {field.unit === "%" ? "%" : ""}
            </Text>
          </Box>
        ))}
      </SimpleGrid>
    </GlassCard>
  );
};

export default Configurations;
